import { useEffect, useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { useTranslation } from '@kostbar/i18n'
import { toast } from 'sonner'
import { SurfaceEditor } from '../components/kiosk-branding/surface-editor'
import {
  useRestaurantKioskBranding,
  useSaveRestaurantKioskBranding,
} from '../hooks/use-restaurant-kiosk-branding'
import type {
  BrandingSurface,
  KioskBranding,
} from '@/api/endpoints/kiosk-branding'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'
import { Button } from '@/components/ui/button'

/** Both surfaces are landscape today: the rear customer display 16:10, the self-checkout 16:9. */
const CUSTOMER_DISPLAY_DEFAULT = { width: 1280, height: 800 }
const SELF_CHECKOUT_DEFAULT = { width: 1920, height: 1080 }

const EMPTY_SURFACE: BrandingSurface = { enabled: false }

export function RestaurantKioskBrandingPage() {
  const { t } = useTranslation('restaurants')
  const { id } = useParams({ strict: false })
  const restaurantId = Number(id)

  const { data, isLoading } = useRestaurantKioskBranding(restaurantId)
  const save = useSaveRestaurantKioskBranding(restaurantId)

  const [customerDisplay, setCustomerDisplay] =
    useState<BrandingSurface>(EMPTY_SURFACE)
  const [selfCheckout, setSelfCheckout] =
    useState<BrandingSurface>(EMPTY_SURFACE)

  useEffect(() => {
    if (!data) return
    setCustomerDisplay(data.customerDisplay ?? EMPTY_SURFACE)
    setSelfCheckout(data.selfCheckout ?? EMPTY_SURFACE)
  }, [data])

  function handleSave() {
    const body: KioskBranding = { customerDisplay, selfCheckout }
    save.mutate(body, {
      onSuccess: (result) => {
        // A save that stored the configuration but failed to publish is not a failure — the next
        // publish carries it — but the operator has to know their screens have not changed yet.
        if (result.published) toast.success(t('branding.saved'))
        else toast.warning(t('branding.savedNotPublished'))
      },
      onError: (error: unknown) => {
        const reason =
          error instanceof Error && error.message
            ? error.message
            : t('branding.saveError')
        toast.error(reason)
      },
    })
  }

  return (
    <PermissionGate action={ACTIONS.RESTAURANT_BRANDING_MANAGE}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">{t('branding.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('branding.subtitle')}</p>
        </div>

        {!isLoading && (
          <>
            <SurfaceEditor
              surface={customerDisplay}
              onChange={setCustomerDisplay}
              title={t('branding.customerDisplay.title')}
              description={t('branding.customerDisplay.description')}
              defaultWidth={CUSTOMER_DISPLAY_DEFAULT.width}
              defaultHeight={CUSTOMER_DISPLAY_DEFAULT.height}
              disabled={save.isPending}
            />
            <SurfaceEditor
              surface={selfCheckout}
              onChange={setSelfCheckout}
              title={t('branding.selfCheckout.title')}
              description={t('branding.selfCheckout.description')}
              defaultWidth={SELF_CHECKOUT_DEFAULT.width}
              defaultHeight={SELF_CHECKOUT_DEFAULT.height}
              withAttractDelay
              disabled={save.isPending}
            />
            <Button onClick={handleSave} disabled={save.isPending}>
              {save.isPending ? t('branding.saving') : t('branding.save')}
            </Button>
          </>
        )}
      </div>
    </PermissionGate>
  )
}
